import React, { useState, forwardRef } from "react";
import {
  User,
  Edit,
  Check,
  X,
  Clock,
  Link,
  ThumbsUp,
  Loader2,
  FileText,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import Button from "../ui/Button";

const MessageBubble = ({
  message,
  currentUserId,
  onReaction,
  isConsecutive = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message?.message_text || "");
  const [showReactions, setShowReactions] = useState(false);

  // Early return if message is undefined - AFTER hooks
  if (!message || !message.id) {
    console.warn(
      "MessageBubble received undefined or invalid message:",
      message
    );
    return null;
  }

  const isOwnMessage = message.sender_id === currentUserId;
  const canEdit =
    isOwnMessage && message.message_type !== "system_notification";
  const canReact = !isOwnMessage; // Anyone can react, but simplified for now

  // ====================
  // MESSAGE TYPE HELPERS
  // ====================

  const getMessageTypeInfo = () => {
    switch (message.message_type) {
      case "drive_link":
        return {
          icon: "🔗",
          label: "Drive Submission",
          color: "text-blue-400",
          bgColor: "bg-blue-500/10",
          borderColor: "border-blue-500/20",
        };
      case "system_notification":
        return {
          icon: "🤖",
          label: "System",
          color: "text-yellow-400",
          bgColor: "bg-yellow-500/10",
          borderColor: "border-yellow-500/20",
        };
      default:
        return {
          icon: "💬",
          label: "Message",
          color: "text-gray-400",
          bgColor: "bg-white/5",
          borderColor: "border-white/10",
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor((now - date) / (1000 * 60));
      return minutes < 1 ? "Just now" : `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // ====================
  // EVENT HANDLERS
  // ====================

  const handleEditSubmit = async () => {
    if (editText.trim() && editText !== message.message_text) {
      // onEdit(message.id, editText.trim()); // This prop is removed
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditText(message.message_text || "");
    setIsEditing(false);
  };

  const handleReactionClick = (reactionType) => {
    if (onReaction) {
      onReaction(message.id, reactionType);
    }
    setShowReactions(false);
  };

  // ====================
  // RENDER HELPERS
  // ====================

  const renderReactions = () => {
    if (!message.reactions || message.reactions.length === 0) return null;

    const reactionGroups = message.reactions.reduce((groups, reaction) => {
      const type = reaction.reaction_type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(reaction);
      return groups;
    }, {});

    const reactionEmojis = {
      thumbs_up: "👍",
      thumbs_down: "👎",
      launched: "🚀",
      needs_revision: "📝",
    };

    return (
      <div className="flex items-center space-x-1 mt-2">
        {Object.entries(reactionGroups).map(([type, reactions]) => (
          <div
            key={type}
            className={`
              flex items-center space-x-1 px-2 py-1 rounded-full text-xs
              ${
                type === "thumbs_up" || type === "launched"
                  ? "bg-green-500/20 text-green-400 border border-green-500/30"
                  : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
              }
            `}
            title={reactions.map((r) => r.user_name).join(", ")}
          >
            <span>{reactionEmojis[type] || "👍"}</span>
            <span>{reactions.length}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderSubmissionStatus = () => {
    if (message.message_type !== "drive_link" || !message.submission_status)
      return null;

    const statusConfig = {
      pending: {
        icon: "⏳",
        text: "Awaiting Review",
        color: "text-yellow-400",
      },
      approved: { icon: "✅", text: "Approved", color: "text-green-400" },
      launched: { icon: "🚀", text: "Launched", color: "text-green-400" },
      rejected: { icon: "❌", text: "Needs Revision", color: "text-red-400" },
    };

    const status = statusConfig[message.submission_status];
    if (!status) return null;

    return (
      <div
        className={`flex items-center space-x-1 text-xs ${status.color} mt-2`}
      >
        <span>{status.icon}</span>
        <span>{status.text}</span>
      </div>
    );
  };

  const renderDriveLink = () => {
    if (!message.drive_link) return null;

    return (
      <div className="mt-2 p-3 bg-white/5 backdrop-blur-xl rounded-lg border border-white/10">
        <div className="flex items-center space-x-2 mb-2">
          <span className="text-lg">📁</span>
          <span className="text-sm font-medium text-white">
            Google Drive Submission
          </span>
        </div>
        <a
          href={message.drive_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 text-sm underline break-all"
        >
          {message.drive_link}
        </a>
        {renderSubmissionStatus()}
      </div>
    );
  };

  // ====================
  // MAIN RENDER
  // ====================

  const messageTypeInfo = getMessageTypeInfo();
  const isSystemMessage = message.message_type === "system_notification";

  // System messages render differently (centered)
  if (isSystemMessage) {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-white/5 backdrop-blur-xl rounded-lg border border-white/10 px-4 py-2 max-w-md text-center">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-300">
            <span>{messageTypeInfo.icon}</span>
            <span>{message.message_text}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex ${isOwnMessage ? "justify-end" : "justify-start"} ${isConsecutive ? "mt-1" : "mt-4"}`}
    >
      <div className={`max-w-[70%] ${isOwnMessage ? "order-2" : "order-1"}`}>
        {/* Sender info (if not consecutive) */}
        {!isConsecutive && (
          <div
            className={`flex items-center space-x-2 mb-1 ${isOwnMessage ? "justify-end" : "justify-start"}`}
          >
            <span className="text-xs font-medium text-gray-300">
              {message.sender_name}
            </span>
            <span className="text-xs text-gray-500">
              {formatTimestamp(message.created_at)}
            </span>
            {message.is_edited && (
              <span className="text-xs text-gray-500 italic">(edited)</span>
            )}
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`
            relative group rounded-2xl px-4 py-3 transition-all duration-200
            ${
              isOwnMessage
                ? "bg-[#B8460E] text-white ml-auto"
                : `${messageTypeInfo.bgColor} ${messageTypeInfo.borderColor} border text-white`
            }
          `}
          onMouseEnter={() => setShowReactions(true)}
          onMouseLeave={() => setShowReactions(false)}
        >
          {/* Message type indicator */}
          {message.message_type !== "text" && (
            <div
              className={`flex items-center space-x-1 text-xs ${messageTypeInfo.color} mb-2`}
            >
              <span>{messageTypeInfo.icon}</span>
              <span>{messageTypeInfo.label}</span>
            </div>
          )}

          {/* Message content */}
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 resize-none"
                rows={3}
                placeholder="Edit your message..."
              />
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleEditSubmit}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Save
                </Button>
                <Button
                  onClick={handleEditCancel}
                  size="sm"
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {message.message_text}
              </p>

              {/* Drive link attachment */}
              {renderDriveLink()}

              {/* Reactions */}
              {renderReactions()}
            </div>
          )}

          {/* Hover actions */}
          {(showReactions || isEditing) && !isEditing && (
            <div
              className={`
              absolute top-0 ${isOwnMessage ? "left-0 -translate-x-full" : "right-0 translate-x-full"} 
              flex items-center space-x-1 bg-black/80 backdrop-blur-xl rounded-lg border border-white/20 px-2 py-1
            `}
            >
              {/* Edit button (own messages only) */}
              {canEdit && (
                <Button
                  onClick={() => setIsEditing(true)}
                  size="sm"
                  variant="ghost"
                  className="text-gray-400 hover:text-white p-1"
                  title="Edit message"
                >
                  ✏️
                </Button>
              )}

              {/* Reaction buttons (media buyers only) */}
              {canReact && (
                <>
                  <Button
                    onClick={() => handleReactionClick("thumbs_up")}
                    size="sm"
                    variant="ghost"
                    className="text-gray-400 hover:text-green-400 p-1"
                    title="Approve / Launch"
                  >
                    👍
                  </Button>
                  <Button
                    onClick={() => handleReactionClick("needs_revision")}
                    size="sm"
                    variant="ghost"
                    className="text-gray-400 hover:text-yellow-400 p-1"
                    title="Needs revision"
                  >
                    📝
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Message shine effect for own messages */}
          {isOwnMessage && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none rounded-2xl"></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
